/**
 * «Duplicar» a route sheet — #178, the passenger half.
 *
 * A sibling of `DecaDuplicateScreen`, not a parameterised version of it: the two read different
 * endpoints, hand their source to different forms and title themselves from different keys, and
 * the URL settles the family. Only a loader: `PassengerCreateForm` with `source` fills a copy and
 * submits it as a create. Any status will do, since the source is only read.
 */

import { useParams } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

import { useAuth } from '../app/auth.tsx'
import { usePassengerDeca } from '../app/queries.ts'
import { abilitiesFor } from '../deca/roles.ts'
import { offeredFamilies } from '../deca/transportType.ts'
import { AppShell } from '../shell/AppShell.tsx'
import { Card, ErrorState, LoadingRows, NoAccessState } from '../ui/primitives.tsx'
import { PassengerCreateForm } from './PassengerCreateForm.tsx'

export function PassengerDuplicateScreen() {
  const { t } = useTranslation()
  const { sheetId } = useParams({ strict: false }) as { sheetId: string }
  const { principal } = useAuth()
  const abilities = abilitiesFor(principal)

  const query = usePassengerDeca(sheetId)
  const source = query.data?.data

  // #220: a copy of a type the company is not offered is not offered by its address either.
  if (!abilities.writeDecas || !offeredFamilies(principal).passenger) {
    return (
      <AppShell title={t('duplicate.sheetTitle')}>
        <Card>
          <NoAccessState />
        </Card>
      </AppShell>
    )
  }

  if (query.isLoading) {
    return (
      <AppShell title={t('duplicate.sheetTitle')}>
        <Card>
          <LoadingRows rows={6} columns={3} />
        </Card>
      </AppShell>
    )
  }

  // Once the source is in, the form stays: a failed background refetch must not replace a copy the
  // user is filling in. The error state is for when there is nothing to copy.
  if (!source) {
    return (
      <AppShell title={t('duplicate.sheetTitle')}>
        <Card>
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        </Card>
      </AppShell>
    )
  }

  return (
    <AppShell title={t('duplicate.sheetTitle')}>
      <PassengerCreateForm source={source} />
    </AppShell>
  )
}
